import { Box, Button, Typography, Grid } from '@mui/material';
import { FiltersToolbar } from '@diana/core';
import { useTheme } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import { FilterType, useFiltersStore, IExtendedFiltersValues } from '../../stores/useFilters';
import { useFilters, createPropertyToFilter } from '../../hooks/useFilters';
import { useCallback, useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MFM_COMPARISON_NS } from './i18n';
import { useFilterOptions } from '../../stores/useFilterOptions';
import { GridCard } from '../Card/Card';
import { LineChart } from '../LineChart/LineChart';
import { LIVE_SESSION_NS } from '../BunkeringDashboard/i18n';
import { NAVIGATION_NS } from '../../i18n/navigation';
import { HubsNames } from '../../stores/useSignalR';
import { StreamDataPointKeys } from '../../types/api/profile';
import { useMessageStore } from '../../stores/useMessageStore';
import { removeQueryIfNoDeviceSelected, convertFiltersToQuery } from '../../utils/convertFiltersToQuery';
import { Device } from './types';
import { DeviceList } from './DeviceList';
import { useSignalRHubStore } from '../../stores/useSignalR';
import { CalendarDatePickerPreset } from '@diana/core';
import { getDefaultTimeRange } from '../../utils/timeRangeUtils';
import { parseQueryToFilters, updateBrowserQueryParams } from '../../utils/urlUtils';

const MFM_COMPARISON_STORE_ID = 'mfm-comparison';

export default function MFMComparison() {
  const { t } = useTranslation([MFM_COMPARISON_NS, LIVE_SESSION_NS, NAVIGATION_NS]);
  
  const {
    filtersValues,
    availableFilters,
    setAvailableFilters,
    setFilters,
    setFiltersValues,
  } = useFiltersStore(MFM_COMPARISON_STORE_ID, {
    [FilterType.MFM_DEVICES]: parseQueryToFilters()[FilterType.MFM_DEVICES],
    timeRange: parseQueryToFilters().timeRange.length > 0 
      ? parseQueryToFilters().timeRange 
      : getDefaultTimeRange(CalendarDatePickerPreset.LastSixHours),
  });

  const bargesData = useFilterOptions.getState().barges || [];
  const [selectedDevices, setSelectedDevices] = useState<Device[]>(() => 
    (filtersValues[FilterType.MFM_DEVICES] || []).map(device => ({
      id: String(device.value),
      name: String(device.displayName),
      bargeName: ''
    }))
  );
  const resetMessages = useMessageStore((state) => state.resetHubMessages);
  const setStreamArgs = useSignalRHubStore((state) => state.setStreamArgs);
  const disposeStream = useSignalRHubStore((state) => state.disposeStream);

  const resetMessagesMemoized = useCallback(
    () => resetMessages(HubsNames.mfmComparsion),
    [resetMessages],
  );

  const updateStreamArgs = useCallback(() => {
    const generatedDevices = selectedDevices.map((device) => ({
      value: device.id,
      displayName: device.name,
    }));
    const transformedFilters = removeQueryIfNoDeviceSelected({
      ...filtersValues,
      [FilterType.MFM_DEVICES]: generatedDevices,
    }, selectedDevices);
    if (transformedFilters !== null) {
      setStreamArgs(HubsNames.mfmComparsion, convertFiltersToQuery(transformedFilters as IExtendedFiltersValues));
    }
  }, [filtersValues, selectedDevices, setStreamArgs]);

  const graphsData = useMessageStore((state) => state?.messages[HubsNames.mfmComparsion] || []);
  const isGraphsLoading = useMessageStore((state) => state.stableLoadingFlags[HubsNames.mfmComparsion]);

  const memoizedGraphsData = useMemo(() => graphsData, [graphsData]);

  const { defaultData, onFilterChange } = useFilters({
    createPropertyToFilter,
    filtersValues: filtersValues as IExtendedFiltersValues,
    setFiltersValues,
    availableFilters: availableFilters.filter(filter => filter.property.key === FilterType.TIME_RANGE),
  });

  const { palette } = useTheme();

  const handleCheckboxChange = useCallback((device: Device) => {
    setSelectedDevices((prevSelected) => {
      const isAlreadySelected = prevSelected.some((d) => d.id === device.id);
      const newSelected = isAlreadySelected
        ? prevSelected.filter((d) => d.id !== device.id)
        : [...prevSelected, device];

      updateBrowserQueryParams({
        ...filtersValues,
        [FilterType.MFM_DEVICES]: newSelected.map(d => ({
          value: d.id,
          displayName: d.name
        }))
      });

      setFiltersValues({...filtersValues, [FilterType.MFM_DEVICES]: newSelected.map(d => ({
        value: d.id,
        displayName: d.name
      }))});

      return newSelected;
    });
  }, [filtersValues, setFiltersValues]);

  const handleCompareClickButton = () => {
    updateStreamArgs();
  };

  const handleResetAll = useCallback(() => {
    setFiltersValues({...filtersValues, [FilterType.MFM_DEVICES]: []});
    setSelectedDevices([]);
  }, [filtersValues, setFiltersValues]);

  useEffect(() => { 
    return () => {
      resetMessagesMemoized();
      disposeStream(HubsNames.mfmComparsion);
    };
  }, [disposeStream, resetMessagesMemoized]);

  return (
    <Box
      sx={{
        mt: '10px',
        mb: '5px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        component="section"
        display="flex"
        justifyContent="center"
        sx={{
          '.MuiStack-root': {
            flex: 1,
            minWidth: 0,
          },
        }}
      >
        <FiltersToolbar
          availableFilters={availableFilters
            .filter(filter => filter.property.key === FilterType.TIME_RANGE)
            .map((filter) => createPropertyToFilter(filter.property, setFiltersValues))}
          defaultData={defaultData}
          filters={[FilterType.TIME_RANGE]}
          filtersValues={filtersValues}
          onFilterChange={onFilterChange}
          setFilters={setFilters}
          setAvailableFilters={setAvailableFilters}
          setFiltersValues={setFiltersValues}
          showAddButton={false}
          showResetButton={false}
          showRemoveButton={false}
        />
      </Box>

      <Box
        className="content"
        sx={{ display: 'flex', flexDirection: 'row', height: 'calc(100vh - 120px)' }}
      >
        <Box
          className="devices"
          sx={{
            pr: 2,
            position: 'relative',
            minWidth: '280px',
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box>
            <Typography
              color={palette.text.primary}
              fontWeight="500"
              fontSize="14px"
              lineHeight="20px"
            >
              {t('components/mfmComparison:mfmDevices')}
            </Typography>
            <Typography
              color={palette.text.secondary}
              fontWeight="400"
              fontSize="12px"
              lineHeight="16px"
              sx={{ marginBottom: '28px' }}
            >
              {t('components/mfmComparison:selectMfmDevices')}
            </Typography>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
            }}
          >
            <DeviceList
              barges={bargesData}
              selectedDevices={selectedDevices}
              onDeviceChange={handleCheckboxChange}
            />
          </Box>

          <Box
            sx={{
              paddingBottom: '18px',
              paddingTop: '12px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Button
              disabled={!selectedDevices.length}
              onClick={handleResetAll}
              sx={{
                color: 'text.secondary',
                padding: '6px 8px',
                minWidth: 'auto',
                '&.Mui-disabled': {
                  color: 'text.secondary',
                },
                '& .MuiSvgIcon-root': {
                  width: '15px',
                  height: '15px',
                  mr: '6px',
                },
              }}
            >
              <CloseIcon />
              {t('components/mfmComparison:resetAll')}
            </Button>
            <Button
              variant="contained"
              disabled={selectedDevices.length === 0}
              sx={{ cursor: 'pointer' }}
              onClick={handleCompareClickButton}
            >
              {t('components/mfmComparison:compare')}
            </Button>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Box component="section">
            <Grid container spacing={1}>
              <Grid item sm={6} xs={12}>
                <GridCard title={t('components/BunkeringDashboard:massFlowRate')} fullscreen={true}>
                  <LineChart
                    dataKey={StreamDataPointKeys.massFlowRate}
                    options={{
                      yAxis: {
                        title: {
                          text: undefined,
                        },
                        labels: {
                          formatter: function () {
                            return `${this.value} MT/hour`;
                          },
                        },
                      },
                    }}
                    data={memoizedGraphsData}
                    loading={isGraphsLoading}
                    noDataDisplay={{
                      subtitle: t('components/mfmComparison:noDataMessage'),
                    }}
                  />
                </GridCard>
              </Grid>
              <Grid item sm={6} xs={12}>
                <GridCard
                  title={t('components/BunkeringDashboard:volumeFlowRate')}
                  fullscreen={true}
                >
                  <LineChart
                    dataKey={StreamDataPointKeys.volumeFlowRate}
                    options={{
                      yAxis: {
                        title: {
                          text: undefined,
                        },
                        labels: {
                          formatter: function () {
                            return `${this.value} m³/hour`;
                          },
                        },
                      },
                    }}
                    data={memoizedGraphsData}
                    loading={isGraphsLoading}
                    noDataDisplay={{
                      subtitle: t('components/mfmComparison:noDataMessage'),
                    }}
                  />
                </GridCard>
              </Grid>
              <Grid item sm={6} xs={12}>
                <GridCard title={t('components/BunkeringDashboard:massTotal')} fullscreen={true}>
                  <LineChart
                    dataKey={StreamDataPointKeys.massTotal}
                    options={{
                      yAxis: {
                        title: {
                          text: undefined,
                        },
                        labels: {
                          formatter: function () {
                            return `${this.value} MT`;
                          },
                        },
                      },
                    }}
                    data={memoizedGraphsData}
                    loading={isGraphsLoading}
                    noDataDisplay={{
                      subtitle: t('components/mfmComparison:noDataMessage'),
                    }}
                  />
                </GridCard>
              </Grid>
              <Grid item sm={6} xs={12}>
                <GridCard title={t('components/BunkeringDashboard:volumeTotal')} fullscreen={true}>
                  <LineChart
                    dataKey={StreamDataPointKeys.volumeTotal}
                    options={{
                      yAxis: {
                        title: {
                          text: undefined,
                        },
                        labels: {
                          formatter: function () {
                            return `${this.value} m³`;
                          },
                        },
                      },
                    }}
                    data={memoizedGraphsData}
                    loading={isGraphsLoading}
                    noDataDisplay={{
                      subtitle: t('components/mfmComparison:noDataMessage'),
                    }}
                  />
                </GridCard>
              </Grid>
              <Grid item sm={6} xs={12}>
                <GridCard title={t('components/BunkeringDashboard:driveGain')} fullscreen={true}>
                  <LineChart
                    dataKey={StreamDataPointKeys.driveGain}
                    options={{
                      yAxis: {
                        title: {
                          text: undefined,
                        },
                        labels: {
                          formatter: function () {
                            return `${this.value} %`;
                          },
                        },
                      },
                    }}
                    data={memoizedGraphsData}
                    loading={isGraphsLoading}
                    noDataDisplay={{
                      subtitle: t('components/mfmComparison:noDataMessage'),
                    }}
                  />
                </GridCard>
              </Grid>
              <Grid item sm={6} xs={12}>
                <GridCard title={t('components/BunkeringDashboard:density')} fullscreen={true}>
                  <LineChart
                    dataKey={StreamDataPointKeys.density}
                    options={{
                      yAxis: {
                        title: {
                          text: undefined,
                        },
                        labels: {
                          formatter: function () {
                            return `${this.value} kg/m³`;
                          },
                        },
                      },
                    }}
                    data={memoizedGraphsData}
                    loading={isGraphsLoading}
                    noDataDisplay={{
                      subtitle: t('components/mfmComparison:noDataMessage'),
                    }}
                  />
                </GridCard>
              </Grid>
              <Grid item sm={6} xs={12}>
                <GridCard title={t('components/BunkeringDashboard:temperature')} fullscreen={true}>
                  <LineChart
                    dataKey={StreamDataPointKeys.temperature}
                    options={{
                      yAxis: {
                        title: {
                          text: undefined,
                        },
                        labels: {
                          formatter: function () {
                            return `${this.value} °C`;
                          },
                        },
                      },
                    }}
                    data={memoizedGraphsData}
                    loading={isGraphsLoading}
                    noDataDisplay={{
                      subtitle: t('components/mfmComparison:noDataMessage'),
                    }}
                  />
                </GridCard>
              </Grid>
              <Grid item sm={6} xs={12}>
                <GridCard title={t('components/BunkeringDashboard:pressure')} fullscreen={true}>
                  <LineChart
                    dataKey={StreamDataPointKeys.pressure}
                    options={{
                      yAxis: {
                        title: {
                          text: undefined,
                        },
                        labels: {
                          formatter: function () {
                            return `${this.value} bar`;
                          },
                        },
                      },
                    }}
                    data={memoizedGraphsData}
                    loading={isGraphsLoading}
                    noDataDisplay={{
                      subtitle: t('components/mfmComparison:noDataMessage'),
                    }}
                  />
                </GridCard>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
